import {Mapper} from '@s-ui/domain'

const DEFAULT_PAGE_VALUE = 1

export class FromSubscriptionUrlVOToSearchRequestVOMapper extends Mapper {
  constructor({config, parseQueryString, searchRequestValueObjectFactory}) {
    super()
    this._config = config
    this._parseQueryString = parseQueryString
    this._searchRequestValueObjectFactory = searchRequestValueObjectFactory
    this._defaultSortBy = this._config.get('SORT_FIELDS').RELEVANCE
    this._defaultSortOrder = this._config.get('ORDER').DESC
    this._sortQueryParams = this._config.get('SORT_QUERY_PARAM')
    this._questionMark = this._config.get('page').QUESTION_MARK
    this._filterNames = this._config.get('search').FILTER_NAMES
    this._offerTypeSubscription = this._config.get('search').OFFER_TYPE_SUBSCRIPTION
    this._adsListSubscriptionPathPatternWithRegex =
      this._config.get('page').ADS_LIST_SUBSCRIPTION_PATH_PATTERN_WITH_REGEX || /^$/
    this._vehicleStateIdSlugMapping = this._config.get('page').VEHICLE_STATE_ID_SLUG_MAPPING
    this._rentingTargetBuyerSlugMapping = this._config.get('page').TARGET_BUYER_SLUG_MAPPING
    this._vehicleTypeSlugMapping = this._config.get('page').VEHICLE_TYPE_SLUG_MAPPING
    this._vehicleTypeCategory1IdMapping = this._config.get('page').VEHICLE_TYPE_CATEGORY1_ID_MAPPING
    this._vehicleStateIds = this._config.get('subscription').VEHICLE_STATE_IDS
    this._seoKeywords = this._config.get('seo').SEO_KEYWORDS
    this._keywordSlugToKeywordMapper = this._config.get('seo').SEO_KEYWORD_SLUG_TO_KEYWORD_MAPPING
    this._keywordSlugToFilterObjectMapper = this._config.get('seo').SEO_KEYWORDS_SLUG_TO_FILTER_OBJECT_MAPPER
    this._seoKeywordSlugRegex = this._config.get('seo').SEO_KEYWORD_SLUG_REGEX
  }

  setParams({contractId, semanticsSearchData}) {
    this._contractId = contractId
    this._semanticsSearchData = semanticsSearchData || {}
    return this
  }

  _getUrlData({url}) {
    const [urlPath, queryParamsString] = url.split(this._questionMark)
    const query = this._parseQueryString(queryParamsString)

    return {query, urlPath}
  }

  _getNotDefault = item => item !== ''

  _getCommitmentMonths = commitmentMonths =>
    commitmentMonths
      ? commitmentMonths
          ?.split('|')
          .filter(this._getNotDefault)
          .map(value => Number(value))
      : undefined

  _getSortOrder = ({or}) => {
    if (or === undefined) return this._defaultSortOrder

    return Number(or) === 1 ? this._config.get('ORDER').ASC : this._config.get('ORDER').DESC
  }

  _getSortBy({fi}) {
    if (!fi) return this._defaultSortBy

    const sortQueryParams = Object.entries(this._sortQueryParams)
    const selectedSortField = sortQueryParams.find(sort => {
      const {1: param} = sort
      return param.toLowerCase() === fi.toLowerCase()
    })

    return selectedSortField ? selectedSortField[0] : fi
  }

  _getFiltersFromUrlPath({urlPath}) {
    const urlPathParams = urlPath.match(this._adsListSubscriptionPathPatternWithRegex)

    // Renting paths deeper than two segments don't match the pattern and carry no segment filters
    if (!urlPathParams) return {}

    const MONTHS_INDEX = 0
    const isCommitmentsSemanticUrl = this._seoKeywordSlugRegex.COMMITMENTS_REGEX.test(urlPath)
    if (isCommitmentsSemanticUrl) {
      const months = parseInt(urlPathParams.at(2).split('-')[MONTHS_INDEX])
      return {
        [this._filterNames.MONTHLY_SUBSCRIPTION_COMMITMENT]: [months]
      }
    }

    let subscriptionVehicleState
    for (const vehicleStateId in this._vehicleStateIdSlugMapping) {
      if (this._vehicleStateIdSlugMapping[vehicleStateId] === urlPathParams.at(2))
        subscriptionVehicleState = this._vehicleStateIds[vehicleStateId]
    }

    const targetBuyer = this._config.get('subscription').TARGET_BUYER

    const rentingTargetBuyer =
      this._rentingTargetBuyerSlugMapping[urlPathParams.at(2)] ||
      this._rentingTargetBuyerSlugMapping[urlPathParams.at(4)]

    const rentingKeywordValue = Object.values(this._seoKeywords).find(
      value => value === urlPathParams.at(2) || value === urlPathParams.at(4)
    )

    const rentingVehicleType =
      this._vehicleTypeSlugMapping[urlPathParams.at(2)] || this._vehicleTypeSlugMapping[urlPathParams.at(4)]

    const hasVehicleState = Boolean(subscriptionVehicleState)
    const hasTargetBuyer = Boolean(rentingTargetBuyer)
    const hasKeyword = Boolean(rentingKeywordValue)

    let rentingPrivateTargetBuyerSort = {}
    let rentingTargetBuyerFilter = {}
    if (hasTargetBuyer) {
      if (rentingTargetBuyer === targetBuyer.BUSINESS) {
        rentingTargetBuyerFilter = {
          targetBuyer: 'business'
        }
      } else if (rentingTargetBuyer === targetBuyer.PRIVATES) {
        const sortByYear = this._config.get('SORT_FIELDS').YEAR
        const sortOrderDesc = this._config.get('ORDER').DESC
        rentingPrivateTargetBuyerSort = {
          sortBy: sortByYear,
          sortOrder: sortOrderDesc
        }
      }
    }
    const hasVehicleType = Boolean(rentingVehicleType)

    return {
      ...(hasVehicleState && {
        subscriptionVehicleState
      }),
      ...(hasTargetBuyer && {
        ...rentingPrivateTargetBuyerSort,
        ...rentingTargetBuyerFilter
      }),
      ...(hasKeyword && this._keywordSlugToFilterObjectMapper[rentingKeywordValue]),
      ...(hasVehicleType && {
        category1Id: this._vehicleTypeCategory1IdMapping[rentingVehicleType]
      })
    }
  }

  map = urlVO => {
    const {url} = urlVO.toJSON()

    const {query, urlPath} = this._getUrlData({url})

    const filtersFromUrlPath = this._getFiltersFromUrlPath({urlPath})

    /**
     * There are the filters which we interpret from FE from the query params
     */
    const {
      commitmentMonths: commitmentMonthsQuery,
      targetBuyer: targetBuyerQuery,
      fi,
      maxFee,
      minFee,
      or,
      pg = DEFAULT_PAGE_VALUE,
      vehicleState,
      Year: year
    } = query

    /**
     * From semantics search data we get the following data:
     */
    const {
      bodyTypeIds,
      commitmentMonths,
      fee,
      fuelTypeIds,
      hasStock,
      makeId,
      model = {},
      subscriptionVehicleState,
      transmissionTypeId,
      vehicles = []
    } = this._semanticsSearchData?.search || {}

    const {id: modelId} = model

    return this._searchRequestValueObjectFactory({
      bodyTypeIds,
      fuelTypeIds,
      commitmentMonths: commitmentMonths || this._getCommitmentMonths(commitmentMonthsQuery),
      ...(this._contractId && {contractId: this._contractId}),
      fee: fee || {from: minFee, to: maxFee},
      hasStock: Boolean(hasStock),
      makeId,
      ...(modelId && {model: {id: modelId}}),
      monthlySubscriptionFee: fee || {from: minFee, to: maxFee},
      offerTypeId: this._offerTypeSubscription,
      page: this._semanticsSearchData?.pagination?.page || Number(pg),
      sortBy: this._semanticsSearchData?.sort?.term || this._getSortBy({fi}),
      sortOrder: this._semanticsSearchData?.sort?.order || this._getSortOrder({or}),
      ...((subscriptionVehicleState || vehicleState) && {
        subscriptionVehicleState: subscriptionVehicleState || vehicleState
      }),
      targetBuyer: targetBuyerQuery,
      transmissionTypeId,
      vehicles,
      year,
      ...filtersFromUrlPath
    })
  }
}
